import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Loader2, Save, X, CheckCircle2, Info, FlaskConical } from "lucide-react";

const STATUS_META = {
  dentro_ideal: { label: "Ideal", cls: "border-success/40 text-success bg-success/5" },
  fora_ideal: { label: "Fora do ideal", cls: "border-amber-500/40 text-amber-600 bg-amber-50 dark:bg-amber-900/10" },
  fora_referencia: { label: "Fora da referência", cls: "border-destructive/40 text-destructive bg-destructive/5" },
  sem_referencia: { label: "Sem referência", cls: "border-muted-foreground/30 text-muted-foreground bg-muted" },
};

export default function AdminExamAnalysisPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [exam, setExam] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // On mount, try to load a saved analysis first; if none, run a fresh analyze.
    (async () => {
      setLoading(true);
      try {
        // Fetch exam meta
        const { data: exams } = await api.get("/admin/exams");
        const ex = (exams || []).find((e) => e.id === examId);
        setExam(ex || null);
        if (ex?.user_id) {
          try {
            const { data: p } = await api.get(`/admin/patients/${ex.user_id}`);
            setPatient(p.patient);
          } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar dados."); }
        }
        // Try saved
        try {
          const { data: saved } = await api.get(`/admin/exams/${examId}/analysis`);
          setAnalysis(saved);
          setLoading(false);
          return;
        } catch (_) { /* fall through to run new */ }
        // Run new analysis
        const { data: fresh } = await api.post(`/admin/exams/${examId}/analyze`);
        setAnalysis(fresh);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Falha ao analisar o exame.");
      }
      setLoading(false);
    })();
  }, [examId]);

  const rerun = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/exams/${examId}/analyze`);
      setAnalysis(data);
      toast.success("Análise recalculada.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao recalcular."); }
    setLoading(false);
  };

  const updateValue = (idx, patch) => {
    setAnalysis((a) => {
      const values = [...a.values];
      values[idx] = { ...values[idx], ...patch };
      return { ...a, values };
    });
  };
  const removeValue = (idx) => setAnalysis((a) => ({ ...a, values: a.values.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!analysis) return;
    setSaving(true);
    try {
      await api.post(`/admin/exams/${examId}/save-analysis`, {
        values: analysis.values,
        matched_patterns: analysis.matched_patterns,
        interpretation_text: analysis.interpretation_text || "",
      });
      toast.success("Análise salva na ficha do paciente.");
      navigate("/admin/exames");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar."); }
    setSaving(false);
  };

  if (loading || !analysis) {
    return (
      <div className="py-20 text-center" data-testid="exam-analysis-loading">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary mb-3" />
        <div className="text-sm text-muted-foreground overline">Analisando exame com IA…</div>
        <div className="text-xs text-muted-foreground mt-1">Isso pode levar até 30 segundos.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exam-analysis-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/admin/exames" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={rerun} className="text-xs font-semibold text-primary hover:underline" data-testid="rerun-analysis">
            Recalcular análise
          </button>
        </div>
      </div>

      <div>
        <div className="overline text-gold mb-2 inline-flex items-center gap-1"><FlaskConical className="w-3.5 h-3.5" /> Resultado da análise</div>
        <h1 className="brand-wordmark text-2xl md:text-3xl font-bold uppercase text-primary" data-testid="analysis-title">
          {exam?.exam_type || "Exame"} — {patient?.name || "Paciente"}
        </h1>
        <div className="text-xs text-muted-foreground mt-1">
          Enviado em {exam?.created_at ? new Date(exam.created_at).toLocaleString("pt-BR") : "—"}
        </div>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-500/40 text-amber-800 dark:text-amber-200 rounded-sm p-3 text-sm flex items-start gap-2" data-testid="disclaimer">
        <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
        <div>Sugestão gerada por IA a partir de padrões cadastrados. <strong>Não substitui avaliação clínica</strong> — revise antes de salvar no prontuário do paciente.</div>
      </div>

      {/* Extracted values table */}
      <section className="bg-card border border-border rounded-sm overflow-hidden" data-testid="values-section">
        <div className="px-4 py-2.5 bg-muted/50 border-b border-border flex items-center justify-between">
          <div>
            <div className="overline text-gold tracking-[0.22em]">Valores extraídos</div>
            <div className="text-[10px] text-muted-foreground">{analysis.values.length} marcador(es) — edite se a extração errar</div>
          </div>
        </div>
        {analysis.values.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">
            Nenhum valor foi extraído automaticamente. Verifique se o arquivo é legível ou preencha manualmente.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="px-4 py-2 overline text-muted-foreground">Marcador</th>
                <th className="px-4 py-2 overline text-muted-foreground">Valor</th>
                <th className="px-4 py-2 overline text-muted-foreground">Unidade</th>
                <th className="px-4 py-2 overline text-muted-foreground">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {analysis.values.map((v, i) => {
                const meta = STATUS_META[v.status] || STATUS_META.sem_referencia;
                return (
                  <tr key={i} className="border-b border-border last:border-0" data-testid={`value-row-${i}`}>
                    <td className="px-4 py-2">
                      <div className="font-semibold">{v.marker_name}</div>
                      {v.extracted_name !== v.marker_name && <div className="text-[10px] text-muted-foreground">extraído: {v.extracted_name}</div>}
                    </td>
                    <td className="px-4 py-2">
                      <input type="number" step="0.001" value={v.value}
                        onChange={(e) => updateValue(i, { value: Number(e.target.value) })}
                        className="w-24 px-2 py-1 rounded-sm border border-input bg-card text-sm" />
                    </td>
                    <td className="px-4 py-2 text-muted-foreground text-xs">{v.unit || "—"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block overline px-2 py-0.5 rounded-sm border ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => removeValue(i)} className="p-1 rounded-sm text-muted-foreground hover:text-destructive" title="Remover"><X className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Matched patterns */}
      <section data-testid="patterns-section">
        <div className="overline text-gold mb-3 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Padrões identificados</div>
        {analysis.matched_patterns.length === 0 ? (
          <div className="bg-card border border-border rounded-sm p-6 text-sm text-muted-foreground text-center">
            Nenhum padrão cadastrado bateu com os valores. Isso não significa que está tudo bem — revise manualmente.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {analysis.matched_patterns.map((p) => (
              <div key={p.id} className="bg-card border border-primary/30 rounded-sm p-4" data-testid={`matched-pattern-${p.id}`}>
                <div className="font-heading font-semibold text-primary">{p.name}</div>
                {p.category && <div className="overline text-gold mt-0.5">{p.category}</div>}
                {p.nutritional_hypotheses && <div className="text-xs mt-2"><span className="overline text-muted-foreground">Hipóteses:</span> {p.nutritional_hypotheses}</div>}
                {p.suggested_tests && <div className="text-xs mt-1"><span className="overline text-muted-foreground">Exames sugeridos:</span> {p.suggested_tests}</div>}
                {p.explanation && <p className="text-xs text-muted-foreground mt-2 whitespace-pre-line">{p.explanation}</p>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Interpretation */}
      <section data-testid="interpretation-section">
        <div className="overline text-gold mb-2 inline-flex items-center gap-1"><Info className="w-3 h-3" /> Interpretação (rascunho)</div>
        <textarea
          rows={7}
          value={analysis.interpretation_text || ""}
          onChange={(e) => setAnalysis({ ...analysis, interpretation_text: e.target.value })}
          className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm leading-relaxed"
          placeholder="Interpretação clínica (edite antes de salvar)…"
          data-testid="interpretation-textarea"
        />
      </section>

      <div className="flex gap-2 justify-end pt-2 border-t border-border">
        <button onClick={() => navigate("/admin/exames")} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted" data-testid="discard-analysis">
          Descartar
        </button>
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
          data-testid="save-analysis-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar na ficha do paciente
        </button>
      </div>
    </div>
  );
}
